import { motion } from "framer-motion";
import {
  Hexagon,
  Dna,
  Activity,
  Atom,
  Sun,
  Shield,
  Sparkles,
  FlaskConical,
  Layers3,
  Microscope,
} from "lucide-react";
import { MoleculeField, PRODUCT_MOLECULE } from "./MoleculeDeco";

const VAULT_BG =
  "https://static.prod-images.emergentagent.com/jobs/513c4d98-c117-415c-aaa0-20587ff2b781/images/9df024c28a989d5e10e06cf5bbac8219a8e0a054c2feea97897e2dcf09ae324a.png";

const products = [
  {
    code: "01",
    name: "Invisible Protein Isolate",
    sub: "Zero-Taste · Zero-Smell · Zero-Color · 100% Soluble",
    Icon: Hexagon,
    tagline: "The 'Ghost' ingredient",
    body: "High-impact nutrition with a zero-taste profile and 100% solubility. It disappears into your formulation while delivering peak performance.",
    span: "md:col-span-2 lg:col-span-2",
    feature: true,
  },
  {
    code: "02",
    name: "I&G",
    sub: "Inosine & Guanosine Monophosphate",
    Icon: Sparkles,
    tagline: "The natural flavor architect",
    body: "Precision taste enhancement for clean-label food products.",
  },
  {
    code: "03",
    name: "CMP",
    sub: "Cytidine Monophosphate",
    Icon: Dna,
    tagline: "The cellular blueprint",
    body: "Essential for cognitive development and neural health in advanced pediatric care.",
  },
  {
    code: "04",
    name: "UMP",
    sub: "Uridine Monophosphate",
    Icon: Activity,
    tagline: "The metabolic engine",
    body: "Driving RNA synthesis for advanced clinical nutrition in geriatric care.",
  },
  {
    code: "05",
    name: "XOS",
    sub: "Xylo-oligosaccharides",
    Icon: Layers3,
    tagline: "The elite prebiotic",
    body: "Engineered for gut-health excellence in specialized infant and senior nutrition.",
  },
  {
    code: "06",
    name: "Glutathione",
    sub: "Master Antioxidant (Reduced)",
    Icon: Atom,
    tagline: "Pharmaceutical grade",
    body: "The ultimate defense against oxidative stress and cellular aging.",
    span: "md:col-span-2 lg:col-span-2",
    feature: true,
  },
  {
    code: "07",
    name: "Vegan Vitamin D2",
    sub: "Ergocalciferol · Plant-derived",
    Icon: Sun,
    tagline: "The Vital Series",
    body: "100% vegan. The sunlight molecule, derived naturally through our proprietary process.",
  },
  {
    code: "08",
    name: "MOS",
    sub: "Mannan Oligosaccharides",
    Icon: Shield,
    tagline: "Immune Architect",
    body: "Precision-engineered to intercept pathogens and fortify natural defenses.",
  },
  {
    code: "09",
    name: "Beta-Glucan",
    sub: "β-1,3/1,6-D-Glucan",
    Icon: Microscope,
    tagline: "Biological response modifier",
    body: "Strengthening systemic immunity at the cellular level.",
  },
  {
    code: "10",
    name: "Designer Peptides",
    sub: "Custom Bio-active Sequences",
    Icon: FlaskConical,
    tagline: "High-performance bio-actives",
    body: "Engineered for novel therapeutic applications and advanced physiological optimization.",
    span: "md:col-span-2 lg:col-span-2",
  },
];

const Card = ({ p, idx }) => {
  const { Icon } = p;
  const Mol = PRODUCT_MOLECULE[p.code];
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.6, delay: 0.05 * (idx % 5) }}
      className={`group/card relative ${p.span || ""}`}
      data-testid={`vault-card-${p.code}`}
    >
      <div className="relative h-full overflow-hidden rounded-xl border border-white/[0.08] bg-gradient-to-br from-white/[0.03] to-white/[0.01] backdrop-blur-md p-6 md:p-8 transition-all duration-500 hover:-translate-y-1 hover:border-[#10B981]/50 hover:shadow-[0_25px_70px_-20px_rgba(16,185,129,0.5)]">
        {/* Card-specific molecule (always faintly visible, big on hover) */}
        {Mol && (
          <motion.div
            initial={{ opacity: 0.18, scale: 0.9, rotate: 0 }}
            animate={{ rotate: 360 }}
            transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
            className="absolute -right-10 -bottom-10 w-44 h-44 md:w-56 md:h-56 opacity-30 group-hover/card:opacity-90 group-hover/card:scale-125 transition-[opacity,transform] duration-700 ease-out"
          >
            <Mol color="#10B981" />
          </motion.div>
        )}

        {/* Soft radial glow on hover */}
        <div className="absolute inset-0 opacity-0 group-hover/card:opacity-100 transition-opacity duration-700 bg-[radial-gradient(circle_at_80%_85%,rgba(16,185,129,0.18),transparent_55%)]" />

        {/* Top row: code + animated icon */}
        <div className="relative flex items-start justify-between">
          <span className="font-display text-[11px] tracking-[0.3em] text-white/40">
            {p.code}
          </span>
          <div className="relative">
            <motion.div
              animate={{ scale: [1, 1.18, 1], opacity: [0.3, 1, 0.3] }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: idx * 0.2 }}
              className="absolute inset-0 -m-3 rounded-full bg-[#10B981]/15 blur-md"
            />
            <Icon
              className="relative h-5 w-5 text-[#10B981] group-hover/card:scale-125 transition-transform duration-500"
              strokeWidth={1.4}
            />
          </div>
        </div>

        {/* Title */}
        <div className="relative mt-12 md:mt-16">
          {p.pct && (
            <div className="font-display text-7xl md:text-8xl font-extralight text-white leading-none mb-3">
              {p.pct}
            </div>
          )}
          <h3 className="font-display text-2xl md:text-3xl font-medium tracking-tight text-white leading-tight">
            {p.name}
          </h3>
          {p.sub && (
            <div className="text-[10px] uppercase tracking-[0.3em] text-white/45 mt-2">
              {p.sub}
            </div>
          )}
          <div className="mt-3 text-[11px] uppercase tracking-[0.25em] text-[#10B981]">
            {p.tagline}
          </div>
          <p className="mt-5 font-body text-sm md:text-base text-white/65 leading-relaxed max-w-md relative z-[1]">
            {p.body}
          </p>
        </div>

        {/* Decorative corner line */}
        <div className="absolute bottom-0 left-0 h-px w-0 bg-[#10B981] transition-all duration-700 group-hover/card:w-full" />
      </div>
    </motion.div>
  );
};

export const Vault = () => {
  return (
    <section
      id="vault"
      data-testid="vault-section"
      className="group/section relative py-24 md:py-40 overflow-hidden"
    >
      {/* Cinematic background */}
      <div className="absolute inset-0 z-0">
        <img
          src={VAULT_BG}
          alt=""
          className="w-full h-full object-cover opacity-25"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#030704] via-[#030704]/85 to-[#030704]" />
      </div>

      {/* Drifting molecule field — auto-moves, scales up on section hover */}
      <MoleculeField scaleOnGroupHover />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-14 md:mb-20 items-end">
          <div className="lg:col-span-6">
            <div className="text-[10px] tracking-[0.4em] uppercase text-[#10B981] flex items-center gap-3 mb-6">
              <span className="h-px w-6 bg-[#10B981]" />
              04 / The Portfolio
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-light text-white leading-[1.02]">
              The Colossus
              <br />
              <span className="italic font-extralight text-white/60">
                Vault<span className="text-[#10B981]">.</span>
              </span>
            </h2>
          </div>
          <div className="lg:col-span-5 lg:col-start-8">
            <p className="font-body text-base md:text-lg text-white/65 leading-relaxed">
              Ten precision molecules. One closed-loop ecosystem. Each
              ingredient is engineered to dissolve the boundary between food,
              pharma and planetary health.
            </p>
          </div>
        </div>

        {/* Bento grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {products.map((p, i) => (
            <Card key={p.code} p={p} idx={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Vault;
